/**
 * SchemeFilter plugin — headless preset filter.
 *
 * Serializes `scheme=kfs|bur` to/from URL, SavedViewFilters and API params.
 * Does NOT render a UI component (placement: "hidden").
 * UI is exposed via preset pills in SavedViewsBar (KFS / BUR / Wszystkie).
 *
 * Semantics:
 * - kfs  → program.type = NATIONAL_FUND only (backend: `andWhere program.type = :type`)
 * - bur  → program.type IS DISTINCT FROM NATIONAL_FUND (includes NULL program)
 *
 * @see docs/specifications/2026-05-27-domyslne-widoki-kfs-bur/spec.md (AC-6, DD-3)
 */

import { FilterPlugin } from "../types";
import { SavedViewFilters, SchemeValue } from "../../../../../services/grantsApi";

const VALID_SCHEMES: ReadonlySet<string> = new Set(["kfs", "bur"]);

export const schemeFilter: FilterPlugin<SchemeValue | undefined> = {
  key: "scheme",
  label: "Schemat",
  placement: "hidden",
  order: 0,
  defaultValue: undefined,
  Component: null,

  isActive(value: SchemeValue | undefined): boolean {
    return value !== undefined;
  },

  toUrlParams(value: SchemeValue | undefined, params: URLSearchParams): void {
    if (value !== undefined) {
      params.set("scheme", value);
    }
  },

  fromUrlParams(params: URLSearchParams): SchemeValue | undefined {
    const raw = params.get("scheme");
    if (raw !== null && VALID_SCHEMES.has(raw)) {
      return raw as SchemeValue;
    }
    return undefined;
  },

  toSavedView(
    value: SchemeValue | undefined,
    filters: Partial<SavedViewFilters>
  ): void {
    if (value !== undefined) {
      filters.scheme = value;
    }
  },

  fromSavedView(filters: SavedViewFilters): SchemeValue | undefined {
    return filters.scheme ?? undefined;
  },

  toApiParams(value: SchemeValue | undefined, params: URLSearchParams): void {
    if (value !== undefined) {
      params.set("scheme", value);
    }
  },
};
